import { formatObservationCount } from '../copy/countNoun';
import { formatSiteDateTime } from '../siteTime/siteTime';

export type ScanVerifyPathArgs = {
  reportId: string;
  orgId?: string | null;
  siteId?: string | null;
  safetyCaseId?: string | null;
  incidentId?: string | null;
  conditionId?: string | null;
};

export function buildScanVerifyPath(args: ScanVerifyPathArgs): string {
  const reportId = String(args.reportId || '').trim();
  if (!reportId) return '/acceptance/cases';
  const params = new URLSearchParams();
  const orgId = String(args.orgId || '').trim();
  const siteId = String(args.siteId || '').trim();
  const safetyCaseId = String(args.safetyCaseId || '').trim();
  const incidentId = String(args.incidentId || '').trim();
  const conditionId = String(args.conditionId || '').trim();
  if (orgId) params.set('org_id', orgId);
  if (siteId) params.set('site_id', siteId);
  if (safetyCaseId) params.set('safety_case_id', safetyCaseId);
  if (incidentId) params.set('incident_id', incidentId);
  if (conditionId) params.set('condition_id', conditionId);
  const qs = params.toString();
  return `/acceptance/scans/${encodeURIComponent(reportId)}/verify${qs ? `?${qs}` : ''}`;
}

/** Canonical buyer Safety Scan home. Identity is exact report_id — never Proof or workflow_id. */
export function safetyScanVerifyPath(
  reportId: string,
  extras: Omit<ScanVerifyPathArgs, 'reportId'> = {},
): string {
  return buildScanVerifyPath({ reportId, ...extras });
}

export function isBuyerProofOpenPath(path: string): boolean {
  return /\/acceptance\/proof(?:\/|\?|$)/.test(String(path || ''));
}

export function parsePendingCountFromBlockers(
  blockers: Array<{ code?: string | null; message?: string | null }> | null | undefined,
): number | null {
  if (!Array.isArray(blockers)) return null;
  const blocker = blockers.find(
    (item) => String(item?.code || '').trim() === 'observation_disposition_pending',
  );
  if (!blocker) return null;
  const message = String(blocker.message || '');
  const match = message.match(/(\d+)\s+(?:candidate|observation)\S*.{0,120}(?:still|need|pending|unreviewed)/i);
  if (!match) return null;
  const count = Number(match[1]);
  return Number.isFinite(count) ? count : null;
}

export function formatScanVerifyProgress(args: {
  reviewedCount?: number | null;
  candidateCount?: number | null;
  pendingCount?: number | null;
}): string {
  const reviewed = Math.max(0, Number(args.reviewedCount || 0));
  const candidate = Math.max(0, Number(args.candidateCount || 0));
  const pending = args.pendingCount == null ? null : Math.max(0, Number(args.pendingCount));
  if (candidate > 0) {
    return `${reviewed} of ${candidate} reviewed`;
  }
  if (pending != null) {
    return `${pending} pending`;
  }
  return 'Progress unavailable';
}

export function formatLatestScanResultHeadline(progress?: {
  candidate_episode_count?: number | null;
  pending_review_count?: number | null;
  reviewed_count?: number | null;
  confirmed_count?: number | null;
} | null): string | null {
  if (!progress) return null;
  const reviewed = Number(progress.reviewed_count);
  const candidates = Number(progress.candidate_episode_count);
  const observations = Number.isFinite(candidates) && candidates > 0
    ? candidates
    : (Number.isFinite(reviewed) ? reviewed : 0);
  if (!observations) return null;
  const confirmed = Number.isFinite(Number(progress.confirmed_count))
    ? Number(progress.confirmed_count)
    : 0;
  const pending = Number.isFinite(Number(progress.pending_review_count))
    ? Number(progress.pending_review_count)
    : 0;
  const reviewLabel = pending > 0 ? `${pending} pending` : 'Review complete';
  return `${formatObservationCount(observations)} · ${confirmed} confirmed · ${reviewLabel}`;
}

export function formatHumanScanWindow(
  start?: string | null,
  end?: string | null,
  timeZone?: string | null,
): string {
  const from = formatSiteDateTime(start, timeZone);
  const to = formatSiteDateTime(end, timeZone);
  if (from && to) return `${from} → ${to}`;
  return from || to || 'Scan window not recorded';
}
